import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon, Phone } from "lucide-react";

const formatErr = (d) => {
  if (!d) return "Qualcosa è andato storto. Riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
};

const AuthDialog = ({ open, onClose, initialTab = "login" }) => {
  const { setUser, loginWithGoogle } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("auth");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const close = () => { onClose(); setTimeout(() => { setBusy(false); setMode("auth"); setForgotSent(false); }, 300); };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error("Errore", { description: formatErr(err?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", login);
      setUser(r.data.user);
      toast.success(`Benvenuto, ${r.data.user.name.split(" ")[0]}!`);
      close();
    } catch (err) {
      toast.error("Accesso fallito", { description: formatErr(err?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (reg.password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    const cleanPhone = (reg.phone || "").replace(/[\s.-]/g, "");
    if (cleanPhone.length < 7) {
      toast.error("Il numero di telefono è obbligatorio");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/register", reg);
      setUser(r.data.user);
      toast.success(`Benvenuto, ${r.data.user.name.split(" ")[0]}!`);
      close();
    } catch (err) {
      toast.error("Registrazione fallita", { description: formatErr(err?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="auth-dialog">
        <div className="px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#032B44]">
              {mode === "forgot" ? "Password dimenticata" : tab === "login" ? "Bentornato" : "Crea il tuo account"}
            </DialogTitle>
            <p className="text-sm text-slate-600 mt-1">
              {mode === "forgot"
                ? "Ti mandiamo un link per reimpostarla."
                : tab === "login" ? "Accedi per vedere i tuoi prezzi personalizzati." : "Un solo minuto per iniziare a ordinare."}
            </p>
          </DialogHeader>
        </div>

        {mode === "forgot" ? (
          <div className="px-6 pb-6 pt-5">
            {forgotSent ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 grid place-items-center mx-auto mb-3">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-700">Se l'email è registrata, riceverai a breve un link per reimpostare la password (valido 1 ora).</p>
                <Button onClick={() => { setMode("auth"); setForgotSent(false); }} className="mt-5 bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="forgot-back-button">
                  Torna al login
                </Button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-3">
                <IconField icon={<Mail />}>
                  <Input type="email" required placeholder="La tua email" value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="pl-9 h-11" data-testid="forgot-email" />
                </IconField>
                <Button type="submit" disabled={busy} className="w-full h-11 bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="forgot-submit">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio…</> : "Invia link di reset"}
                </Button>
                <button type="button" onClick={() => setMode("auth")} className="w-full text-xs text-slate-500 hover:text-[#032B44]" data-testid="forgot-cancel">
                  ← Torna indietro
                </button>
              </form>
            )}
          </div>
        ) : (
          <>
        <div className="px-6 pt-5">
          <button
            type="button"
            onClick={loginWithGoogle}
            className="w-full h-11 rounded-lg border border-sky-200 bg-white hover:bg-sky-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium text-[#032B44]"
            data-testid="auth-google-button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1A6.97 6.97 0 015.48 12c0-.73.13-1.43.36-2.1V7.07H2.18A10.99 10.99 0 001 12c0 1.77.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continua con Google
          </button>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-sky-100 flex-1" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-sky-600">oppure</span>
            <div className="h-px bg-sky-100 flex-1" />
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="px-6 pb-6">
          <TabsList className="grid grid-cols-2 bg-sky-50 p-1 rounded-lg w-full mb-5">
            <TabsTrigger value="login" data-testid="auth-tab-login">Accedi</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Registrati</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={submitLogin} className="space-y-3">
              <IconField icon={<Mail />}>
                <Input type="email" required placeholder="Email" value={login.email}
                  onChange={(e) => setLogin({ ...login, email: e.target.value })}
                  className="pl-9 h-11" data-testid="auth-login-email" />
              </IconField>
              <IconField icon={<Lock />}>
                <Input type="password" required placeholder="Password" value={login.password}
                  onChange={(e) => setLogin({ ...login, password: e.target.value })}
                  className="pl-9 h-11" data-testid="auth-login-password" />
              </IconField>
              <Button type="submit" disabled={busy} className="w-full h-11 bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="auth-login-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Accesso…</> : "Accedi"}
              </Button>
              <button
                type="button"
                onClick={() => { setForgotEmail(login.email); setMode("forgot"); }}
                className="w-full text-xs text-sky-700 hover:text-[#00A3FF]"
                data-testid="forgot-password-link"
              >
                Password dimenticata?
              </button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={submitRegister} className="space-y-3">
              <IconField icon={<UserIcon />}>
                <Input required placeholder="Nome e cognome" value={reg.name}
                  onChange={(e) => setReg({ ...reg, name: e.target.value })}
                  className="pl-9 h-11" data-testid="auth-register-name" />
              </IconField>
              <IconField icon={<Mail />}>
                <Input type="email" required placeholder="Email" value={reg.email}
                  onChange={(e) => setReg({ ...reg, email: e.target.value })}
                  className="pl-9 h-11" data-testid="auth-register-email" />
              </IconField>
              <IconField icon={<Phone />}>
                <Input type="tel" required placeholder="Telefono" value={reg.phone}
                  onChange={(e) => setReg({ ...reg, phone: e.target.value })}
                  className="pl-9 h-11" data-testid="auth-register-phone" />
              </IconField>
              <IconField icon={<Lock />}>
                <Input type="password" required placeholder="Password (min. 6 caratteri)" value={reg.password}
                  onChange={(e) => setReg({ ...reg, password: e.target.value })}
                  className="pl-9 h-11" data-testid="auth-register-password" />
              </IconField>
              <Button type="submit" disabled={busy} className="w-full h-11 bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="auth-register-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creazione…</> : "Crea account"}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">Registrandoti accetti i termini di servizio.</p>
            </form>
          </TabsContent>
        </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const IconField = ({ icon, children }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-600">
      {React.cloneElement(icon, { className: "w-4 h-4" })}
    </span>
    {children}
  </div>
);

export default AuthDialog;
